/**
 * ImmoLens — Rules Fallback (Frontend)
 *
 * Statische Kopie von rv-plugin-backend/config/rules.js als ES-Module.
 * Wird verwendet wenn GET /api/v1/rules nicht erreichbar ist.
 *
 * ACHTUNG: Manuell synchron zu rv-plugin-backend/config/rules.js halten.
 * Keine Logik — nur Datendefinition.
 */

const rulesFallback = {

  // ─── 1. Verdicts ────────────────────────────────────────────────────────────
  verdicts: [
    {
      key: 'top',
      label: 'Top Investment',
      score_min: 80,
      score_max: 100,
      color_token: 'verdict-green',
      verdict_sentence: 'Top Investment — starke Kennzahlen.'
    },
    {
      key: 'good',
      label: 'Gutes Investment',
      score_min: 65,
      score_max: 79,
      color_token: 'verdict-green',
      verdict_sentence: 'Gutes Investment — solide Grundlage.'
    },
    {
      key: 'check',
      label: 'Prüfen',
      score_min: 50,
      score_max: 64,
      color_token: 'verdict-yellow',
      verdict_sentence: 'Prüfen — Chancen und Risiken abwägen.'
    },
    {
      key: 'risky',
      label: 'Riskant',
      score_min: 35,
      score_max: 49,
      color_token: 'verdict-orange',
      verdict_sentence: 'Riskant — kritische Faktoren vorhanden.'
    },
    {
      key: 'pass',
      label: 'Abraten',
      score_min: 0,
      score_max: 34,
      color_token: 'verdict-red',
      verdict_sentence: 'Abraten — hohe Risiken, kein klares Upside.'
    }
  ],

  // ─── 2. KPI Rules ───────────────────────────────────────────────────────────
  kpi_rules: {

    cashflow: {
      label: 'Monatlicher Cashflow',
      unit: '€/Monat',
      conditions: [
        { threshold: 200,        operator: '>',  text: 'starker positiver Cashflow',                        badge: 'gut',        value_color_token: 'value-green' },
        { threshold: 0,          operator: '>=', text: 'positiv',                                           badge: 'akzeptabel', value_color_token: 'value-green' },
        { threshold: -100,       operator: '>=', text: 'leicht negativ — durch Preisabschlag kompensiert', badge: 'akzeptabel', value_color_token: 'value-red' },
        { threshold: -Infinity,  operator: '<',  text: 'deutlich negativ',                                  badge: 'kritisch',   value_color_token: 'value-red' }
      ]
    },

    brutto_rendite: {
      label: 'Brutto-Rendite',
      unit: '%',
      conditions: [
        { threshold: 5, operator: '>=', text: 'stark',   badge: 'gut',        value_color_token: 'text-dark' },
        { threshold: 4, operator: '>=', text: 'gut',     badge: 'akzeptabel', value_color_token: 'text-dark' },
        { threshold: 3, operator: '>=', text: 'solide',  badge: 'akzeptabel', value_color_token: 'text-dark' },
        { threshold: 0, operator: '<',  text: 'niedrig', badge: 'kritisch',   value_color_token: 'text-dark' }
      ]
    },

    netto_rendite: {
      label: 'Netto-Rendite',
      unit: '%',
      conditions: [
        { threshold: 5, operator: '>=', text: 'stark',   badge: 'gut',        value_color_token: 'text-dark' },
        { threshold: 4, operator: '>=', text: 'gut',     badge: 'akzeptabel', value_color_token: 'text-dark' },
        { threshold: 3, operator: '>=', text: 'solide',  badge: 'akzeptabel', value_color_token: 'text-dark' },
        { threshold: 0, operator: '<',  text: 'niedrig', badge: 'kritisch',   value_color_token: 'text-dark' }
      ]
    },

    ek_rendite: {
      label: 'EK-Rendite',
      unit: '%',
      conditions: [
        { threshold: 8, operator: '>=', text: 'attraktiv — durch günstigen Einstieg', badge: 'gut',        value_color_token: 'value-green' },
        { threshold: 5, operator: '>=', text: 'gut',                                  badge: 'akzeptabel', value_color_token: 'value-green' },
        { threshold: 0, operator: '>=', text: 'moderat',                              badge: 'akzeptabel', value_color_token: 'text-dark' },
        { threshold: 0, operator: '<',  text: 'negativ',                              badge: 'kritisch',   value_color_token: 'value-red' }
      ]
    },

    kaufpreisfaktor: {
      label: 'Kaufpreisfaktor',
      unit: 'x',
      conditions: [
        { threshold: 20, operator: '<=', text: 'günstig',    badge: 'gut' },
        { threshold: 25, operator: '<=', text: 'akzeptabel', badge: 'akzeptabel' },
        { threshold: 25, operator: '>',  text: 'teuer',      badge: 'kritisch' }
      ]
    }
  },

  // ─── 3. Color Tokens ────────────────────────────────────────────────────────
  color_tokens: {
    // Verdict colors
    'verdict-green':  '#22c55e',
    'verdict-yellow': '#eab308',
    'verdict-orange': '#f97316',
    'verdict-red':    '#ef4444',

    // Value colors
    'value-green':    '#0D9668',
    'value-red':      '#DC2626',
    'value-orange':   '#D97706',

    // Brand colors
    'primary-cyan':   '#1DB3B3',
    'cyan-light':     '#E8F7F7',
    'cyan-dark':      '#0E7A6B',

    // Text colors
    'text-dark':      '#0F172A',
    'text-heading':   '#1E293B',
    'text-muted':     '#94A3B8',
    'text-secondary': '#64748B',
    'text-warning':   '#9A6B14',

    // Badge backgrounds
    'bg-positive':    '#ECFDF5',
    'bg-medium':      '#FEF8EC',
    'bg-critical':    '#FEF2F2',
    'bg-info':        '#F8FAFC',
    'bg-highlight-pos': '#F0FDF4',
    'bg-highlight-neg': '#FFFBEB',

    // Signal medium
    'signal-medium-bg':   '#FEF8EC',
    'signal-medium-text': '#9A6B14',
    'signal-medium-dot':  '#D97706'
  },

  // ─── 4. Insight Rules ───────────────────────────────────────────────────────
  insight_rules: [
    { id: 1,  conditions: { deal_good: true, cf_positive: false }, text: 'Günstiger Einstieg, aber laufende Zuzahlung nötig' },
    { id: 2,  conditions: { deal_good: true, cf_positive: true },  text: 'Guter Einstieg mit positivem Cashflow' },
    { id: 3,  conditions: { deal_good: true, a_city: true },       text: 'Unter Markt in nachfragestarker Lage' },
    { id: 4,  conditions: { deal_good: true },                     text: 'Attraktiver Einstiegspreis' },
    { id: 5,  conditions: { deal_bad: true, cf_positive: true },   text: 'Positiver Cashflow, aber hoher Einstiegspreis' },
    { id: 6,  conditions: { deal_bad: true },                      text: 'Überhöhter Preis belastet die Rendite' },
    { id: 7,  conditions: { cf_positive: true, a_city: true },     text: 'Solides Investment mit stabiler Lage' },
    { id: 8,  conditions: { cf_positive: true },                   text: 'Positiver Cashflow als Basis' },
    { id: 9,  conditions: { cf_negative: true, a_city: true },     text: 'Starker Standort, aber monatliche Zuzahlung' },
    { id: 10, conditions: { a_city: true },                        text: 'Nachfragestarker Standort' },
    { id: 11, conditions: { cf_negative: true },                   text: 'Negativer Cashflow belastet die Rendite' },
    { id: 12, conditions: {},                                       text: 'Solides Renditeprofil' }
  ],

  // ─── 5. Insight Thresholds ──────────────────────────────────────────────────
  insight_thresholds: {
    deal_good_threshold:   -0.10,
    deal_bad_threshold:     0.15,
    cf_positive_threshold:  0,
    cf_negative_threshold:  0
  },

  // ─── 6. Chip Rules ──────────────────────────────────────────────────────────
  chip_rules: {
    positive: {
      style: {
        background_token: 'cyan-light',
        color_token:      'cyan-dark',
        border_radius:    '6px',
        padding:          '3px 9px',
        font_size:        '11px',
        font_weight:      '500'
      },
      conditions: [
        { condition: 'price_below_market', threshold: -0.05, text_template: '−{X}% vs. Markt' },
        { condition: 'cashflow_positive',  threshold: 0,     text_template: 'CF +{X} €/M' },
        { condition: 'city_a',                               text_template: 'A-Stadt' },
        { condition: 'city_b',                               text_template: 'B-Stadt' }
      ]
    },
    negative: {
      style: {
        background_token: 'bg-medium',
        color_token:      'text-warning',
        border_radius:    '6px',
        padding:          '3px 9px',
        font_size:        '11px',
        font_weight:      '500'
      },
      conditions: [
        { condition: 'price_above_market', threshold: 0.05, text_template: '+{X}% vs. Markt' },
        { condition: 'cashflow_negative',  threshold: 0,    text_template: 'CF {X} €/M' }
      ]
    }
  },

  // ─── 7. Signal Badge Styles ─────────────────────────────────────────────────
  signal_badge_styles: {
    positive: { dot_color: '#0D9668', background: '#ECFDF5', color: '#0D9668', label: 'Positiv' },
    critical: { dot_color: '#DC2626', background: '#FEF2F2', color: '#DC2626', label: 'Kritisch' },
    medium:   { dot_color: '#D97706', background: '#FEF8EC', color: '#9A6B14', label: 'Mittel' },
    info:     { dot_color: '#94A3B8', background: '#F8FAFC', color: '#64748B', label: 'Hinweis' }
  },

  // ─── 8. Price Comparison ────────────────────────────────────────────────────
  price_comparison: {
    gradient: 'linear-gradient(90deg, #1AB3B3 0%, #E8C94A 50%, #E06464 100%)',
    below_market: {
      text_template: '{X}% unter dem Durchschnitt',
      color_token:   'value-green'
    },
    above_market: {
      text_template: '{X}% über dem Durchschnitt',
      color_token:   'value-red'
    }
  },

  // ─── 9. Score Bar Thresholds ────────────────────────────────────────────────
  score_bar_thresholds: [
    { min_pct: 70, bar_color_token: 'primary-cyan', value_color: '#0F172A' },
    { min_pct: 40, bar_color_token: 'value-orange',  value_color: '#92400E' },
    { min_pct: 0,  bar_color_token: 'value-red',     value_color: '#DC2626' }
  ],

  // ─── 10. Scenario Slider Thresholds ─────────────────────────────────────────
  scenario_slider_thresholds: [
    { min_score: 65, color_token: 'primary-cyan' },
    { min_score: 50, color_token: 'value-orange' },
    { min_score: 0,  color_token: 'value-red' }
  ]
};

export default rulesFallback;
